import { ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { TreeEntity } from 'src/app/core/models/entity.model';
import { EntityEditorPropertyChangedEvent } from 'src/app/entity-editor/models/entity-editor-property-changed.event';
import { ProjectDataActions } from '../../store/project-data/project-data.actions';
import { PropertyGroupType } from 'src/app/core/models/property-group-type.model';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from 'src/app/shared/components/confirm-dialog/confirm-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { EntityTypeKey } from 'src/app/shared/constants/entity-type-keys';
import { selectOpenHoodLinePanelIds } from '../../store/product-view/product-view.selectors';
import { ProductViewActions } from '../../store/product-view/product-view.actions';
import { ActiconInteractiveComponentHandler } from 'src/app/visualize3d/v3d-components/acticon-interactive/acticon-interactive-component-handler';
import { selectProjectFeedbackMessages } from '../../store/project-data/project-data.selectors';

@Component({
  selector: 'app-hoodline',
  templateUrl: './hoodline-view.component.html',
  styleUrls: ['./hoodline-view.component.scss'],
  standalone: false
})
export class HoodLineComponent implements OnInit, OnChanges, OnDestroy {
  selectedId = '';
  selectedEntity: TreeEntity;
  initialized = true;

  protected hoodlines: TreeEntity[];
  openHoodLines$: Observable<string[]>;

  feedbackMessages$: Observable<TreeEntity[]>;

  @Input() projectEntity: TreeEntity | null;
  @Input() filterGroups: any[];
  @Input() groupFilter: (group: PropertyGroupType) => boolean;

  navigationSubscription: Subscription;

  constructor(
    private store: Store,
    private route: ActivatedRoute,
    private router: Router,
    private changeDetector: ChangeDetectorRef,
    private dialog: MatDialog,
    private translate: TranslateService,
    private acticonInteractiveComponentHandler: ActiconInteractiveComponentHandler
  ) {
    this.navigationSubscription = this.router.events.subscribe((e: any) => {
      if (e instanceof NavigationEnd) {
        this.initializeComponent();
      }
    });
    this.openHoodLines$ = this.store.select(selectOpenHoodLinePanelIds);
    this.feedbackMessages$ = this.store.select(selectProjectFeedbackMessages);
  }

  initializeComponent() {
    this.initialized = false;
    this.changeDetector.detectChanges();
    this.selectedId = this.route.snapshot.queryParamMap.get('selectedId');
    this.initialized = true;
    this.setSelectedEntity();
  }

  ngOnInit() {
    this.initializeComponent();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['projectEntity']) {
      this.setSelectedEntity();
    }
  }

  ngOnDestroy(): void {
    if (this.navigationSubscription) {
      this.navigationSubscription.unsubscribe();
    }
  }

  setSelectedEntity() {
    if (this.selectedId && this.projectEntity) {
      this.hoodlines = this.findInTreeById(this.projectEntity, this.selectedId).Children.filter(
        f => f.EntityTypeKey === EntityTypeKey.GastroFlowHoodLine || f.EntityTypeKey === EntityTypeKey.GastroFlowHoodConnectionLine
      );
    } else {
      this.hoodlines = null;
    }
  }

  findInTreeById(selector: TreeEntity, id: string): TreeEntity {
    if (selector === null) {
      return null;
    }
    if (selector.Id === id) {
      return selector;
    }
    for (const child of selector.Children) {
      const candidate = this.findInTreeById(child, id);
      if (candidate !== null) {
        return candidate;
      }
    }
    return null;
  }

  expand(id: string): void {
    this.store.dispatch(ProductViewActions.open_hoodline({ id, closeOthers: false }));
    this.acticonInteractiveComponentHandler.addToSelectedEntityIds(id);
  }

  collapse(id: string): void {
    this.store.dispatch(ProductViewActions.close_hoodline({ id }));
    this.acticonInteractiveComponentHandler.removeFromSelectedEntityIds(id);
  }

  isOpen(openHoodlineIds: string[], id): boolean {
    return openHoodlineIds.includes(id);
  }

  deleteLine(id: number): void {
    const textId = this.translate.instant('GastroFlowHoodLine.DeleteLine.ActionConfirmation');

    const dialogRef = this.dialog.open(ConfirmDialogComponent, { data: { textId } });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.store.dispatch(
          ProjectDataActions.delete_entity_start({
            entityIdList: [id.toString()]
          })
        );
      }
    });
  }

  hasMessages(hoodLine: TreeEntity, feedbackMessages: TreeEntity[], messageLevel: number): boolean {
    const hoodlineMessages = hoodLine.Children.filter(
      child => child.EntityTypeKey === EntityTypeKey.Message && child.Properties['Level']?.Value === messageLevel
    );

    const panelMessages = this.getPanelMessages(hoodLine, feedbackMessages).filter(m => m.Properties['Level']?.Value === messageLevel);

    return hoodlineMessages.length + panelMessages.length > 0;
  }

  getPanelMessages(hoodLine: TreeEntity, feedbackMessages: TreeEntity[]): TreeEntity[] {
    const lineNumber = hoodLine.Properties['Number'].Value as number;

    return feedbackMessages.filter(message => {
      if (message.Parent?.EntityTypeKey === EntityTypeKey.GastroFlowOutsidePanel && message.Parent?.Parent) {
        const module = message.Parent?.Parent;

        if (module) {
          const moduleLineNumber = module.Properties['LineNumber'].Value as number;

          return moduleLineNumber === lineNumber;
        }
      }

      return false;
    });
  }

  propertyChanged(event: EntityEditorPropertyChangedEvent, hoodlineId: string): void {
    this.store.dispatch(
      ProjectDataActions.update_project_start({
        entityId: hoodlineId,
        propertyKey: event.propertyKey,
        newValue: event.value,
        measurementUnit: event.measurementUnit
      })
    );
  }
}
